/**
 * formulario.ts — Categoria "Formulário" (10 modelos premium)
 *
 * Captura de contato em seções com camadas: texto de apoio, prova social,
 * fotos do acervo e o bloco de formulário já configurado.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote B)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { AVATARES, FOTO, WEB, b } from './_shared';

/** Atalho para o bloco de formulário. */
function form(titulo: string, apoio = '', botao = 'Enviar', identificador = 'lead_capture') {
  return b('page.form', {
    formId: identificador,
    title: titulo,
    description: apoio,
    submitLabel: botao,
    successType: 'message',
    successTitle: 'Obrigado!',
    successMessage: 'Recebemos seus dados. Em breve entraremos em contato.',
  });
}

const modelos: StudioPatternDefinition[] = [
  {
    key: 'formulario.captura',
    category: 'formulario',
    label: 'Captura simples',
    description: 'Formulário centralizado com título curto.',
    preview: 'form',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '720px', motion: 'fade' }, [
        form('Fale com a gente', 'Preencha os dados e retornamos no mesmo dia.'),
      ]),
    ],
  },

  {
    key: 'formulario.com-texto',
    category: 'formulario',
    label: 'Formulário com argumento',
    description: 'Texto de venda à esquerda e formulário à direita.',
    preview: 'split-right',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { bleed: 'contained', layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Receba uma proposta sob medida', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Sem compromisso: entendemos a sua necessidade e enviamos os valores por escrito.' }),
        form('Solicitar proposta', '', 'Quero a proposta'),
      ]),
    ],
  },

  {
    key: 'formulario.escuro',
    category: 'formulario',
    label: 'Formulário em fundo escuro',
    description: 'Bloco escuro com brilho, bom para fechar a página.',
    preview: 'form',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'xl', align: 'center', maxWidth: '820px', backgroundGradient: 'mesh-dark', glow: 'center', textColor: '#ffffff', radius: 'xl', motion: 'fade' },
        [
          b('ui.title', { text: 'Vamos começar?', size: 'lg', level: 'h2', align: 'center', accentColor: '#7dd3fc' }),
          form('Deixe seu contato', 'Retornamos em poucos minutos no horário comercial.'),
        ],
      ),
    ],
  },

  {
    key: 'formulario.com-foto',
    category: 'formulario',
    label: 'Formulário com foto',
    description: 'Foto de atendimento ao lado do formulário.',
    preview: 'split-left',
    outputs: WEB,
    order: 4,
    build: () => [
      b('section.layered', { bleed: 'contained', layout: 'split-reverse', paddingY: 'lg', align: 'left', maxWidth: '1120px', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        b('ui.image', { src: FOTO.atendimento, alt: 'Equipe de atendimento', ratio: 'portrait', radius: 'xl', shadow: 'lg' }),
        form('Fale com um especialista', 'Um consultor entra em contato para entender o seu cenário.'),
      ]),
    ],
  },

  {
    key: 'formulario.com-prova-social',
    category: 'formulario',
    label: 'Formulário com prova social',
    description: 'Fotos de clientes e nota média acima do formulário.',
    preview: 'form',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '820px', motion: 'fade' }, [
        b('ui.avatars', { items: AVATARES, text: '+2.000 clientes atendidos', note: 'Nota 4,9 de 5 em avaliações', align: 'center' }),
        form('Quero ser atendido', 'Sem compromisso e sem custo.'),
      ]),
    ],
  },

  {
    key: 'formulario.com-beneficios',
    category: 'formulario',
    label: 'Formulário com benefícios',
    description: 'Três motivos para preencher, ao lado do formulário.',
    preview: 'split-right',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { bleed: 'contained', layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade-up' }, [
        b('ui.card', { icon: '✓', title: 'Resposta no mesmo dia', text: 'Sem espera e sem robô.', variant: 'outline', radius: 'lg' }),
        b('ui.card', { icon: '✓', title: 'Proposta por escrito', text: 'Valores claros, sem letra miúda.', variant: 'outline', radius: 'lg' }),
        b('ui.card', { icon: '✓', title: 'Sem compromisso', text: 'Você decide no seu tempo.', variant: 'outline', radius: 'lg' }),
        form('Solicitar contato'),
      ]),
    ],
  },

  {
    key: 'formulario.sobre-foto',
    category: 'formulario',
    label: 'Formulário sobre a foto',
    description: 'Foto de fundo escurecida com o formulário por cima.',
    preview: 'form',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { bleed: 'contained', backgroundImage: FOTO.escritorio, overlay: 'darker', paddingY: 'xl', align: 'center', textColor: '#ffffff', maxWidth: '820px', motion: 'fade' },
        [form('Peça uma demonstração', 'Mostramos o sistema funcionando com dados do seu segmento.', 'Agendar demonstração', 'demo_request')],
      ),
    ],
  },

  {
    key: 'formulario.orcamento',
    category: 'formulario',
    label: 'Pedido de orçamento',
    description: 'Formulário voltado a orçamento, com selo e prazo.',
    preview: 'form',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '820px', backgroundGradient: 'soft', radius: 'xl', motion: 'fade-up' }, [
        b('ui.badge', { text: 'Orçamento sem custo', variant: 'soft', align: 'center' }),
        form('Pedir orçamento', 'Retorno em até 24 horas úteis.', 'Pedir orçamento', 'quote_request'),
      ]),
    ],
  },

  {
    key: 'formulario.newsletter',
    category: 'formulario',
    label: 'Assinatura de novidades',
    description: 'Captura curta para conteúdos e novidades.',
    preview: 'form',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'md', align: 'center', maxWidth: '720px', backgroundColor: '#f8fafc', radius: 'xl', motion: 'fade' }, [
        form('Receba nossas novidades', 'Conteúdo prático, no máximo uma vez por semana.', 'Quero receber', 'newsletter'),
      ]),
    ],
  },

  {
    key: 'formulario.completo',
    category: 'formulario',
    label: 'Formulário de fechamento',
    description: 'Título, argumento, número de prova e formulário.',
    preview: 'form',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'xl', align: 'center', maxWidth: '900px', pattern: 'grid', motion: 'fade-up' }, [
        b('ui.title', { text: 'O último passo é com você', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.stat', { value: '7 min', label: 'de tempo médio de resposta', align: 'center' }),
        form('Falar com o time', 'Preencha os dados e continuamos a conversa por onde preferir.'),
      ]),
    ],
  },
];

registerPatterns(modelos);
